import { MsgExecuteContract } from "cosmes/client";
import { useWallet } from "providers/WalletProvider";
import { useCallback, useEffect, useState } from "react"
import { useFetcher, useNavigate, useParams } from "react-router-dom"
import { DENOM } from "utils/Constants";
import { formatDenom, formatNumber, getSymbol } from "utils/Functions"
import { queryBalance, querySmartContract } from "utils/RpcHelper"

function debounce(func: Function, delay: number) {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func(...args);
        }, delay);
    };
}

export function SwapPage() {
    const { address, post } = useWallet()
    const navigate = useNavigate()
    const { pairAddress } = useParams()
    const [tradeInfo, setTradeInfo] = useState<{ offerAsset: string, askAsset: string }>();
    const [assetInfos, setAssetInfos] = useState<{ denom?: string, contractAddr?: string, symbol: string, balance?: number }[]>()
    const [amount, setAmount] = useState('');
    const [returnAmount, setReturnAmount] = useState(0)


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAmount(value);
        debouncedSearch(value);
    };

    const simulateSwap = async (value: string) => {
        const amountNumber = Number(value)

        if (amountNumber > 0 && tradeInfo && assetInfos) {
            const offerAssetInfo = assetInfos.find(e => e.contractAddr == tradeInfo.offerAsset || e.denom == tradeInfo.offerAsset)!

            let offerAsset = {}
            if (offerAssetInfo.denom) {
                offerAsset = {
                    native_token: {
                        denom: offerAssetInfo.denom
                    }
                }
            } else {
                offerAsset = {
                    token: {
                        contract_addr: offerAssetInfo.contractAddr
                    }
                }
            }

            const { return_amount } = await querySmartContract(pairAddress!, {
                simulation: {
                    offer_asset: {
                        info: offerAsset,
                        amount: (amountNumber * 1e6).toString()
                    },
                }
            })
            setReturnAmount(Number(return_amount))
        } else {
            setReturnAmount(0)
        }
    }

    const debouncedSearch = useCallback(debounce(simulateSwap, 500), [tradeInfo, assetInfos]);


    const loadPairInfo = async () => {
        try {
            const { asset_infos } = await querySmartContract(pairAddress!, { pair: {} })
            if (asset_infos) {
                const firstAsset = await getSymbol(asset_infos[0])
                const secondAsset = await getSymbol(asset_infos[1])
                let firstBalance, secondBalance = 0;

                for (const asset of asset_infos) {
                    let balance;

                    if ("token" in asset) {
                        const { balance: tokenBalance } = await querySmartContract(asset.token.contract_addr, {
                            balance: {
                                address: address
                            }
                        })
                        balance = tokenBalance
                    } else if ("native_token" in asset) {
                        balance = await queryBalance(address!, asset.native_token.denom)
                    }

                    if (asset_infos.indexOf(asset) === 0) firstBalance = Number(balance)
                    else if (asset_infos.indexOf(asset) === 1) secondBalance = Number(balance)
                }

                setAssetInfos([
                    {
                        contractAddr: asset_infos[0].token?.contract_addr,
                        denom: asset_infos[0].native_token?.denom,
                        symbol: firstAsset,
                        balance: firstBalance
                    },
                    {
                        contractAddr: asset_infos[1].token?.contract_addr,
                        denom: asset_infos[1].native_token?.denom,
                        symbol: secondAsset,
                        balance: secondBalance
                    }
                ])
            }
        } catch (e) {
            console.log(e)
            alert("Cannot load pair info")
        }
    }

    useEffect(() => {
        if (address) loadPairInfo()
    }, [address])

    useEffect(() => {
        if (assetInfos && !tradeInfo)
            setTradeInfo({
                offerAsset: (assetInfos[0].contractAddr ?? assetInfos[0].denom)!,
                askAsset: (assetInfos[1].contractAddr ?? assetInfos[1].denom)!
            })
    }, [assetInfos])

    const swapTradeInfo = () => {
        if (tradeInfo) {
            let _tradeInfo = { ...tradeInfo };
            _tradeInfo.askAsset = tradeInfo.offerAsset;
            _tradeInfo.offerAsset = tradeInfo.askAsset;
            setTradeInfo(_tradeInfo)
        }
    }

    useEffect(() => {
        simulateSwap(amount)
    }, [tradeInfo])


    const formatToSymbol = (denomOrContract?: string) => {
        return assetInfos?.find(e => e.denom == denomOrContract || e.contractAddr == denomOrContract)?.symbol;
    }

    const swap = async () => {
        if (!address) return alert("Please connect your wallet first")
        if (returnAmount == 0) return alert("Amount must be number")

        const offerAssetInfo = assetInfos!.find(e => e.contractAddr == tradeInfo!.offerAsset || e.denom == tradeInfo!.offerAsset)!
        const amountNumber = Math.trunc(Number(amount) * 1e6)
        const balance = offerAssetInfo.balance ?? 0

        if (!balance || balance < amountNumber) return alert("Your balance is not enough")

        let offerAsset = {}
        if (offerAssetInfo.denom) {
            offerAsset = {
                native_token: {
                    denom: offerAssetInfo.denom
                }
            }
        } else {
            offerAsset = {
                token: {
                    contract_addr: offerAssetInfo.contractAddr
                }
            }
        }

        try {
            const msg = {
                swap: {
                    offer_asset: {
                        info: offerAsset,
                        amount: amountNumber.toString()
                    },
                }
            };

            let txHash = undefined;
            if ("token" in offerAsset) {

                const executeMsg = new MsgExecuteContract({
                    sender: address,
                    contract: (offerAsset.token as any).contract_addr,
                    funds: [],
                    msg: {
                        send: {
                            contract: pairAddress,
                            msg: Buffer.from(JSON.stringify(msg)).toString("base64"),
                            amount: amountNumber.toString()
                        }
                    },
                })
                txHash = await post({
                    msgs: [executeMsg],
                    memo: `Swap ${amount} ${formatDenom(assetInfos![0].symbol)} to ${assetInfos![1].symbol}`
                }, [])
            } else {
                const executeMsg = new MsgExecuteContract({
                    sender: address,
                    contract: pairAddress!,
                    funds: [{ amount: amountNumber.toString(), denom: tradeInfo?.offerAsset! }],
                    msg: msg,
                })
                const tax = { denom: DENOM, amount: Math.trunc(amountNumber * 0.005) }
                txHash = await post({
                    msgs: [executeMsg],
                    memo: `Swap ${amount} ${formatDenom(assetInfos![0].symbol)} to ${assetInfos![1].symbol}`
                }, [tax])
            }

            if (txHash) navigate("/tx-loading/" + txHash)
        } catch (e) {
            console.log(e)
            alert("error: " + e + ", (PLEASE REPORT TO DEVELOPER)")
        }
    }

    return <div className="w-100 p-2 px-4">
        <h4>Swap asset</h4>
        <p className="text-muted">Swap your asset</p>

        <div className="d-flex flex-row">
            <div className="form-group flex-grow-1">
                <label>Offer Asset</label>
                <span className="form-control">{formatToSymbol(tradeInfo?.offerAsset)}</span>
            </div>

            <button onClick={swapTradeInfo} className="mt-auto mb-3 mx-3 btn btn-icon btn-primary">
                <i className="mdi mdi-swap-horizontal"></i>
            </button>


            <div className="form-group flex-grow-1">
                <label>Ask Asset</label>
                <span className="form-control">{formatToSymbol(tradeInfo?.askAsset)}</span>
            </div>
        </div>


        <div className="form-group">
            <label htmlFor="token-address">Amount</label>
            <input
                onChange={handleChange}
                type="number"
                className="form-control"
                id="token-address"
                placeholder={`Enter ${formatToSymbol(tradeInfo?.offerAsset)} amount to swap`} />
            <span className="text-muted">Your balance: {formatNumber((assetInfos?.find(e => e.denom == tradeInfo?.offerAsset || e.contractAddr == tradeInfo?.offerAsset)?.balance ?? 0) / 1e6)} {formatToSymbol(tradeInfo?.offerAsset)}</span>
        </div>

        <div className="form-group">
            <label htmlFor="token-address">you will receive</label>
            <span className="form-control">{formatNumber((returnAmount ?? 0) / 1e6)} {formatToSymbol(tradeInfo?.askAsset)}</span>
        </div>

        <button onClick={swap} type="button" className="btn btn-primary w-100 btn-lg">Swap</button>
    </div>
}